import { useState } from "react";
import { DateGrid } from "Components/atoms/DateGrid";
import { Typography } from "Components/atoms/Typography";
import { useCalendarContext } from "shared/contexts/CalendarContext";
import { useSearchContext } from "shared/contexts/SearchContext";
import { getFormattedDateDetails } from "shared/utils/getFormattedDateDetails";
import { generateDaysInMonth } from "shared/utils/generateDaysInMonth";
import { DatePickerProps } from "../types";
import {
  DatePickerAreaMobile,
  DatePickerContainerMobile,
  SelectionModeButtonMobile,
  SelectionModeTabsMobile,
} from "./styles";

const MONTHS_TO_SHOW = 12;

export const DatePickerMobile: React.FC<DatePickerProps> = ({
  selectedDates = [],
  onDatesChange,
  maxSelectableDates,
  type,
}) => {
  const {
    currentDate,
    selectionMode,
    setSelectionMode,
    selectedOriginDates,
    selectedDestinationDates,
    setSelectedOriginDates,
    setSelectedDestinationDates,
  } = useCalendarContext();
  const { searchState, setDepartureDates, setReturnDates } = useSearchContext();
  const [departure, setDeparture] = useState<string | null>(null);

  const selectDate = (date: string) => {
    if (searchState.tripType === "oneWay") {
      setDeparture(null);
      setSelectedOriginDates([date]);
      setSelectedDestinationDates([]);
      setDepartureDates([date]);
      setReturnDates([]);
      return;
    }

    if (!departure) {
      setDeparture(date);
      setSelectedOriginDates([date]);
      setSelectedDestinationDates([]);
      setDepartureDates([date]);
      setReturnDates([]);
      return;
    }

    const [start, end] =
      departure <= date ? [departure, date] : [date, departure];
    setSelectedOriginDates([start]);
    setSelectedDestinationDates([end]);
    setDepartureDates([start]);
    setReturnDates([end]);
    setDeparture(null);
  };

  const start = selectedOriginDates[0];
  const end = selectedDestinationDates[0];
  const visibleSelectedDates =
    selectionMode === "single"
      ? [...selectedOriginDates, ...selectedDestinationDates]
      : selectedDates;

  const selectMultipleDate = (date: string) => {
    const updatedDates = selectedDates.includes(date)
      ? selectedDates.filter((selectedDate) => selectedDate !== date)
      : selectedDates.length < maxSelectableDates
        ? [...selectedDates, date]
        : selectedDates;
    onDatesChange(updatedDates);
  };

  const grayDates =
    type === "origin" ? selectedDestinationDates : selectedOriginDates;

  const changeSelectionMode = (mode: "single" | "multi") => {
    if (mode === selectionMode) return;
    setDeparture(null);
    setSelectedOriginDates([]);
    setSelectedDestinationDates([]);
    setDepartureDates([]);
    setReturnDates([]);
    setSelectionMode(mode);
  };

  return (
    <DatePickerAreaMobile>
      <SelectionModeTabsMobile
        role="tablist"
        aria-label="Modo de seleção de datas"
      >
        <SelectionModeButtonMobile
          type="button"
          role="tab"
          aria-selected={selectionMode === "single"}
          isActive={selectionMode === "single"}
          onClick={() => changeSelectionMode("single")}
        >
          {searchState.tripType === "oneWay" ? "Só Ida" : "Ida e Volta"}
        </SelectionModeButtonMobile>
        <SelectionModeButtonMobile
          type="button"
          role="tab"
          aria-selected={selectionMode === "multi"}
          isActive={selectionMode === "multi"}
          onClick={() => changeSelectionMode("multi")}
        >
          Várias datas
        </SelectionModeButtonMobile>
      </SelectionModeTabsMobile>
      {Array.from({ length: MONTHS_TO_SHOW }, (_, offset) => {
        const displayedDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + offset,
          1,
        );
        const formatted = getFormattedDateDetails(displayedDate);
        const days = generateDaysInMonth(
          displayedDate.getFullYear(),
          displayedDate.getMonth(),
          selectedOriginDates,
          selectedDestinationDates,
        );
        return (
          <div
            key={`${displayedDate.getFullYear()}-${displayedDate.getMonth()}`}
          >
            <DatePickerContainerMobile>
              <Typography variant="h3" color="black">
                {formatted.monthFull}
              </Typography>
              <Typography variant="h3" color="black">
                {formatted.year}
              </Typography>
            </DatePickerContainerMobile>
            <DateGrid
              days={days}
              currentDate={displayedDate}
              selectedDates={visibleSelectedDates}
              onDatesChange={() => {}}
              onSelectDate={(day) => {
                const date = new Date(
                  displayedDate.getFullYear(),
                  displayedDate.getMonth(),
                  day,
                );
                const formattedDate = `${date.getFullYear()}-${String(
                  date.getMonth() + 1,
                ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                if (selectionMode === "single") selectDate(formattedDate);
                else selectMultipleDate(formattedDate);
              }}
              rangeStart={selectionMode === "single" ? start : undefined}
              rangeEnd={selectionMode === "single" ? end : undefined}
              type={selectionMode === "multi" ? type : undefined}
              grayDates={selectionMode === "multi" ? grayDates : undefined}
              continuousRange={selectionMode === "single"}
            />
          </div>
        );
      })}
    </DatePickerAreaMobile>
  );
};
